import { createContext, useContext, useMemo } from "react";
import {
    GridTable,
    GridTableHeader,
    GridTableRow,
} from "../GridTable/GridTable";

interface SimpleTableProps {
    className?: string;
    columns: Array<{
        key: string;
        label: React.ReactNode;
    }>;
    rows: Array<{
        id: string;
        cells: Array<{
            value: React.ReactNode;
            onClick?: () => void;
        }>;
        onClick?: () => void;
    }>;
    columnBorders?: boolean;
    fullWidth?: boolean;
    gridTemplateColumns?: string;
}

/** @deprecated Use @design-system/ViewTable/ViewTable for product tables. SimpleTable is deprecated; use SpreadsheetGrid only for spreadsheet-style cell editing. */
export const SimpleTable = ({
    className,
    columns,
    rows,
    columnBorders,
    fullWidth,
    gridTemplateColumns,
}: SimpleTableProps) => {
    const context = useMemo(
        () => ({ columns, columnBorders }),
        [columns, columnBorders],
    );

    return (
        <SimpleTableContext.Provider value={context}>
            <GridTable
                className={className}
                fullWidth={fullWidth}
                gridTemplateColumns={gridTemplateColumns}
            >
                <SimpleTableHeader />
                {rows.map((row) => (
                    <SimpleTableRow
                        onClick={row.onClick}
                        key={row.id}
                        cells={row.cells}
                    />
                ))}
            </GridTable>
        </SimpleTableContext.Provider>
    );
};

interface SimpleTableContextType {
    columns: Array<{
        key: string;
        label: string | React.ReactNode;
    }>;
    columnBorders?: boolean;
}

const SimpleTableContext = createContext<SimpleTableContextType>({
    columns: [],
});

const SimpleTableHeader = () => {
    const { columns, columnBorders } = useContext(SimpleTableContext);
    return <GridTableHeader columns={columns} columnBorders={columnBorders} />;
};

interface SimpleTableRowProps {
    cells: Array<{
        value: string | React.ReactNode;
    }>;
    onClick?: () => void;
}

const SimpleTableRow = ({ cells, onClick }: SimpleTableRowProps) => {
    const { columns, columnBorders } = useContext(SimpleTableContext);
    return (
        <GridTableRow
            cells={cells.map((cell, index) => ({
                key: columns[index].key,
                ...cell,
            }))}
            onClick={onClick}
            columnBorders={columnBorders}
        />
    );
};
